/** Bulk Delete on outline lines/tasks — confirm once; each server delete owns its hierarchy cascade. */
import { ItemDeletedResponse } from '@jms/model';
import { EntityDataService } from '@workbuddy/services/entities';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { SemanticCommand } from '@workbuddy/ui/services/keyboard.shortcuts';
import { ToastService } from '@workbuddy/ui/services';
import { BulkCommandArgs, BulkCommandOutcome } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { OutlineEntityKinds, OutlineRow } from '../outline.authoring.model';
import { isOutlineServiceLine, outlineMoveTaskIds } from '../outline.selection';
import type { OutlineRowPatcher } from './outline.bulk.schedule';

export const OUTLINE_DELETE_ACTION = SemanticCommand.Delete;

export interface OutlineDeleteDeps {
  /** The entity pair this run is acting on, so one handler serves the live outline and the template one. */
  kinds: OutlineEntityKinds;
  confirm: ConfirmServicePro;
  entityData: EntityDataService;
  store: OutlineRowPatcher;
  toasts: Pick<ToastService, 'success' | 'error'>;
}

export async function handleOutlineDelete(args: BulkCommandArgs<OutlineRow>, deps: OutlineDeleteDeps): Promise<BulkCommandOutcome> {

  if ((args.bulkActionRequest?.actionId ?? args.action) !== OUTLINE_DELETE_ACTION) return { handled: false };
  const lineRows = (args.data ?? []).filter(row => isOutlineServiceLine(row, deps.kinds));
  const lineIds = new Set(lineRows.map(row => row.sourceId));
  // A task whose own line is also selected rides with it: the line delete cascades its whole subtree,
  // so sending the task too would ask the server to remove a row that no longer exists.
  const taskRows = (args.data ?? []).filter(row => {
    const lineId = deps.kinds.lineRef(row)?._id;
    return !lineId || !lineIds.has(lineId);
  });
  const taskIds = outlineMoveTaskIds(taskRows, deps.kinds);
  const count = lineRows.length + taskIds.length;
  if (!count) return { handled: true, clearSelection: false };
  const singleLine = count === 1 && lineRows.length === 1;
  const singleTask = count === 1 && taskIds.length === 1;
  const question = singleLine
    ? `Delete '${lineRows[0].name}' and all of its tasks and items? This cannot be undone.`
    : singleTask
      ? `Delete '${nameOf(args.data, taskIds[0])}' and any subtasks? This cannot be undone.`
      : `Delete ${count} selected service lines or tasks and their children? This cannot be undone.`;
  const title = singleLine ? 'Delete service line' : singleTask ? 'Delete task' : 'Delete selection';
  if (!await deps.confirm.confirm(title, question, 'danger')) return { handled: true, clearSelection: false };

  try {
    for (const target of [...lineRows.map(row => ({ entity: deps.kinds.line, id: row.sourceId })), ...taskIds.map(id => ({ entity: deps.kinds.task, id }))]) {
      const result = await deps.entityData.getDelete(target.entity).mutateAsync({ id: target.id }) as ItemDeletedResponse;
      if (result?.success !== true) {
        deps.toasts.error('Delete failed', result?.error ?? 'The selection could not be deleted.');
        return { handled: true, clearSelection: false };
      }
    }
  } catch (error) {
    deps.toasts.error('Delete failed', error instanceof Error ? error.message : 'The selection could not be deleted.');
    return { handled: true, clearSelection: false };
  }

  deps.toasts.success(singleLine ? 'Service line deleted' : singleTask ? 'Task deleted' : 'Selection deleted', count === 1 ? lineRows[0]?.name ?? nameOf(args.data, taskIds[0]) : `${count} items`);
  deps.store.setFocusedItemId?.(null);
  deps.store.$gridApi?.()?.refreshServerSide?.({ purge: true });
  return { handled: true };
}

function nameOf(rows: readonly OutlineRow[] | undefined, id: string): string {

  return rows?.find(row => row.sourceId === id)?.name ?? 'task';
}
